import {
  DuplicateInfoDto,
  reportErrorToServer,
  subscribeToNewsletter,
  useGetActiveCreditAppLinkedToCellPhoneOrEmail,
  useSaveDraftApplicantInfo,
} from '@applyonline/shared'
import { FORM_ID, PhoneInput, ScrollableAlert } from '@applyonline/shared/components'
import { useFormAutoSave } from '@applyonline/shared/utils'
import { yupResolver } from '@hookform/resolvers/yup'
import { EFinancingProgram, ELanguage, EOriginSystemId } from '@if/core/types'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import ValidationErrorMessage from '../../../../../packages/shared/validator/ValidationErrorMessage'
import { PersonalInformationInput, PersonalInformationInputSchema } from '../../../types/Schemas/PrequalificationSchema'

interface Props {
  prequalificationData: PersonalInformationInput
  onPrequalificationChanged: (data: PersonalInformationInput) => void
  nextStepAfter: (currentStep: string) => void
  currentStep: string
  merchantId: string
}

const PersonalInformationForm = ({
  prequalificationData,
  onPrequalificationChanged,
  nextStepAfter,
  currentStep,
  merchantId,
}: Props) => {
  const { t } = useTranslation()
  const [showLegalNameCheckbox, setShowLegalNameCheckbox] = useState(false)

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    watch,
    control,
    subscribe,
  } = useForm<PersonalInformationInput>({
    mode: 'onBlur',
    resolver: yupResolver(PersonalInformationInputSchema),
    defaultValues: prequalificationData,
  })

  useFormAutoSave(subscribe, onPrequalificationChanged)

  const watchFirstName = watch('firstName')
  const watchLastName = watch('lastName')

  useEffect(() => {
    if (watchFirstName && watchLastName && watchFirstName.trim() === watchLastName.trim()) {
      setShowLegalNameCheckbox(true)
    } else {
      setShowLegalNameCheckbox(false)
      setValue('hasLegalName', false)
    }
  }, [watchFirstName, watchLastName, setValue])

  const [activeCreditAppLinkedToCellPhoneOrEmail] = useGetActiveCreditAppLinkedToCellPhoneOrEmail(EFinancingProgram.B2c)
  const [saveDraftApplicantInfo] = useSaveDraftApplicantInfo()

  const [hasDuplicateLoanRequest, setHasDuplicateLoanRequest] = useState(false)

  const onSubmit: SubmitHandler<PersonalInformationInput> = async (data) => {
    const dto: DuplicateInfoDto = {
      email: data.email,
      cellPhone: data.cellPhone,
    }
      const isDuplicateRequest = await activeCreditAppLinkedToCellPhoneOrEmail(dto)
      if (isDuplicateRequest === false) {
      onPrequalificationChanged(data)
      nextStepAfter(currentStep)
      if (data.subscribeNewsletter) {
        try {
          await subscribeToNewsletter({
            ...data,
            languageId: data.languageId,
            financingProgramId: EFinancingProgram.B2c,
            originSystemId: EOriginSystemId.B2c,
          })
        } catch (error) {
          reportErrorToServer(error as Error)
        }
      }
      try {
        void saveDraftApplicantInfo({
          firstName: data.firstName,
          lastName: data.lastName,
          middleName: data.middleName,
          email: data.email.toLowerCase(),
          phoneNumber: data.cellPhone,
          otherPhoneNumber: data.homePhone,
          languageId: data.languageId,
          merchantId: merchantId,
          financingProgramId: EFinancingProgram.B2c,
        })
      } catch (error) {
        reportErrorToServer(error as Error)
      }
    } else {
      setHasDuplicateLoanRequest(true)
    }    
  }

  return (
    <section className="step-content">
      <h3 className="form-question">{t('personalInformation.aboutYourself')}</h3>

      <form onSubmit={handleSubmit(onSubmit, (errors) => console.error(errors))} id={FORM_ID}>
        <ScrollableAlert
          showAlert={hasDuplicateLoanRequest}
          type="warn"
          message={t('contact.hasActiveLoanRequest')}
          link={t('contact.icebergPhone')}
          linkText={t('contact.icebergPhone')}
          linkType="tel"
        />

        <div className={`control-group ${errors?.languageId && 'error'}`}>
          <label htmlFor="radiobuttons">{t('personalInformation.preferredLanguage')}</label>
          <div className="radiobuttons-wrap">
            <div className="radio-wrap">
              <input
                type="radio"
                value={ELanguage.French}
                id="French"
                {...register('languageId')}
                defaultChecked={prequalificationData.languageId === ELanguage.French}
              />
              <label htmlFor="French">{t(`ELanguage.${ELanguage.French}`, { ns: 'enum' })}</label>
            </div>
            <div className="radio-wrap">
              <input
                type="radio"
                value={ELanguage.English}
                id="English"
                {...register('languageId')}
                defaultChecked={prequalificationData.languageId === ELanguage.English}
              />
              <label htmlFor="English">{t(`ELanguage.${ELanguage.English}`, { ns: 'enum' })}</label>
            </div>
          </div>
        </div>

        <div className="control-group">
          <div className="control-group-wrap">
            <div className={`control-group first-name third sm-full ${errors?.firstName && 'error'}`}>
              <label htmlFor="firstName">
                {t('personalInformation.yourFirstName')}
                <span className="form-hint">{t('personalInformation.nameHint')}</span>
              </label>
              <input type="text" id="firstName" {...register('firstName')} maxLength={50} autoComplete='given-name' />
              <ValidationErrorMessage message={errors?.firstName?.message} />
            </div>

            <div className={`control-group last-name third sm-full ${errors?.lastName && 'error'}`}>
              <label htmlFor="lastName">
                {t('personalInformation.yourLastName')}
                <span className="form-hint">{t('personalInformation.nameHint')}</span>
              </label>
              <input type="text" id="lastName" {...register('lastName')} maxLength={50} autoComplete='family-name' />
              <ValidationErrorMessage message={errors?.lastName?.message} />
            </div>

            <div className={`control-group middle-name third sm-full ${errors?.middleName && 'error'}`}>
              <label htmlFor="middleName">
                {t('personalInformation.yourMiddleName')}
                <span className="form-hint">{t('personalInformation.optional')}</span>
              </label>
              <input type="text" id="middleName" {...register('middleName')} maxLength={50} autoComplete='new-password' data-Lpignore='true' data-1p-ignore='true' />
              <ValidationErrorMessage message={errors?.middleName?.message} />
            </div>
          </div>
          {showLegalNameCheckbox && (
            <div className="control-group" style={{ paddingTop: '0.5rem', paddingBottom: '1.5rem' }}>
              <input
                type="checkbox"
                id="useLegalName"
                {...register('hasLegalName')}
                onChange={(e) => {
                  setValue('hasLegalName', e.target.checked)
                }}
              />
              <label htmlFor="useLegalName">{t('personalInformation.LegalName')}</label>
            </div>
          )}
        </div>
        <div></div>

        <div className={`control-group  ${errors?.email && 'error'}`}>
          <label htmlFor="email">
            {t('contact.emailAddress')}
            <span className="help-tip">
              <p>{t('contact.emailAddressInfo')}</p>
            </span>
          </label>
          <input id="email" type="text" inputMode="email" autoComplete="email" {...register('email')} maxLength={60} />
          <ValidationErrorMessage message={errors?.email?.message} />
        </div>

        <div className="control-group">
          <div className="control-group-wrap" style={{ alignItems: 'start' }}>
            <PhoneInput className="mid" control={control} name="cellPhone" label={t('contact.mobilePhone')} />
            <PhoneInput className="mid" control={control} name="homePhone" label={t('contact.otherPhone')} />
          </div>
        </div>

        {/*  SUBSCRIBE NEWSLATTER AFTER MVP !IMPORTANT */}
        <div className="clarification">
          <p>
            {t('contact.newsletter')}
            <u>
              <a href={t('common.policyLink')} target="_blank" rel="noreferrer">
                {t('contact.linkText')}
              </a>
              .
            </u>
          </p>
        </div>
        <div className="control-group">
          <input type="checkbox" id="newsletter" {...register('subscribeNewsletter')} />
          <label htmlFor="newsletter">{t('contact.subscribe')}</label>
        </div>
      </form>
    </section>
  )
}

export default PersonalInformationForm
